import React, { useState } from "react";
import { API } from "../api";
import "../styles/style_securityManager.css";
import { hashPassword } from "../utils/password";

export default function SecurityManager({ user, setUser, message, setMessage }) {
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [codeDigits, setCodeDigits] = useState(["", "", "", "", "", ""]);
  const [isCodeSubmitting, setIsCodeSubmitting] = useState(false);
  const [emailData, setEmailData] = useState({
    oldEmail: user?.email || "",
    newEmail: "",
    confirmEmail: "",
  });
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
  const [showOldEmailCodeInput, setShowOldEmailCodeInput] = useState(false);
  const [showNewEmailCodeInput, setShowNewEmailCodeInput] = useState(false);
  const [oldEmailCodeDigits, setOldEmailCodeDigits] = useState(["", "", "", "", "", ""]);
  const [newEmailCodeDigits, setNewEmailCodeDigits] = useState(["", "", "", "", "", ""]);
  const [isOldEmailCodeSubmitting, setIsOldEmailCodeSubmitting] = useState(false);
  const [isNewEmailCodeSubmitting, setIsNewEmailCodeSubmitting] = useState(false);
  const [a2f_enabled, setA2f_enabled] = useState(user?.login_metadata?.twoFA_enabled || false);
  const [show2FAConfirm, setShow2FAConfirm] = useState(false);
  const [is2FASubmitting, setIs2FASubmitting] = useState(false);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
    setMessage({ type: "", text: "" });
  };

  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setEmailData((prev) => ({ ...prev, [name]: value }));
    setMessage({ type: "", text: "" });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });

    // Validations
    if (!passwordData.oldPassword) {
      setMessage({ type: "error", text: "Veuillez entrer votre ancien mot de passe" });
      return;
    }

    if (!passwordData.newPassword) {
      setMessage({ type: "error", text: "Veuillez entrer votre nouveau mot de passe" });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setMessage({ type: "error", text: "Le mot de passe doit contenir au moins 8 caractères" });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: "error", text: "Les mots de passe ne correspondent pas" });
      return;
    }

    setIsPasswordSubmitting(true);

    try {
      const response = await API.put("user/personalUpdate", { uuid: user?.uuid, email: user?.email, type: "password" }, { oldPassword: await hashPassword(passwordData.oldPassword) });

      if (response.success) {
        if (response.code_required) {
          setShowCodeInput(true);
          setCodeDigits(["", "", "", "", "", ""]);
          setMessage({ type: "info", text: "Un code de vérification a été envoyé" });
        } else {
          setPasswordData({ oldPassword: "", newPassword: "", confirmPassword: "" });
          setShowPasswords({ old: false, new: false, confirm: false });
          setMessage({ type: "success", text: "Mot de passe changé avec succès !" });
        }
      } else setMessage({ type: "error", text: response.message || "Erreur lors du changement de mot de passe" });
    } catch (error) {
      setMessage({ type: "error", text: error.response?.data?.message || "Erreur lors du changement de mot de passe" });
      console.error(error);
    } finally {
      setIsPasswordSubmitting(false);
    }
  };

  const handleToggle2FA = () => {
    setShow2FAConfirm(true);
  };

  const handleCancel2FA = () => {
    setShow2FAConfirm(false);
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;

    const newDigits = [...codeDigits];
    newDigits[index] = value;
    setCodeDigits(newDigits);

    if (value && index < 5) {
      const nextInput = document.getElementById(`code-digit-${index + 1}`);
      if (nextInput) nextInput.focus();
    }

    setMessage({ type: "", text: "" });
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (!codeDigits[index] && index > 0) {
        const prevInput = document.getElementById(`code-digit-${index - 1}`);
        if (prevInput) {
          prevInput.focus();
          const newDigits = [...codeDigits];
          newDigits[index - 1] = "";
          setCodeDigits(newDigits);
        }
      }
    }
  };

  const handleCancelCode = () => {
    setShowCodeInput(false);
    setCodeDigits(["", "", "", "", "", ""]);
    setMessage({ type: "", text: "" });
  };

  const handleSubmitCode = async () => {
    const code = codeDigits.join("");

    if (code.length !== 6) {
      setMessage({ type: "error", text: "Veuillez saisir un code à 6 chiffres" });
      return;
    }

    setIsCodeSubmitting(true);

    try {
      const response = await API.put("user/personalUpdate", { uuid: user?.uuid, email: user?.email, type: "passwordCode" }, { token: code, oldPassword: await hashPassword(passwordData.oldPassword), newPassword: await hashPassword(passwordData.newPassword) });

      if (response.success) {
        setPasswordData({ oldPassword: "", newPassword: "", confirmPassword: "" });
        setShowPasswords({ old: false, new: false, confirm: false });
        setShowCodeInput(false);
        setCodeDigits(["", "", "", "", "", ""]);
        setMessage({ type: "success", text: "Mot de passe changé avec succès !" });
      } else setMessage({ type: "error", text: response.message || "Code de vérification invalide" });
    } catch (error) {
      setMessage({ type: "error", text: error.response?.data?.message || "Erreur lors de la vérification du code" });
      console.error(error);
    } finally {
      setIsCodeSubmitting(false);
    }
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });

    if (!emailData.newEmail) {
      setMessage({ type: "error", text: "Veuillez entrer votre nouvel email" });
      return;
    }

    if (emailData.newEmail !== emailData.confirmEmail) {
      setMessage({ type: "error", text: "Les emails ne correspondent pas" });
      return;
    }

    if (emailData.newEmail === emailData.oldEmail) {
      setMessage({ type: "error", text: "Le nouvel email doit être différent de l'ancien" });
      return;
    }

    setIsEmailSubmitting(true);

    try {
      const response = await API.put("user/personalUpdate", { uuid: user?.uuid, email: user?.email, type: "email" }, { newEmail: emailData.newEmail });

      if (response.success) {
        if (response.code_required) {
          setShowOldEmailCodeInput(true);
          setOldEmailCodeDigits(["", "", "", "", "", ""]);
          setMessage({ type: "info", text: "Un code de vérification a été envoyé à vos deux emails" });
        } else {
          setEmailData({ oldEmail: emailData.newEmail, newEmail: "", confirmEmail: "" });
          setUser((prev) => ({ ...prev, email: emailData.newEmail }));
          setMessage({ type: "success", text: "Email changé avec succès !" });
        }
      } else setMessage({ type: "error", text: response.message || "Erreur lors du changement d'email" });
    } catch (error) {
      setMessage({ type: "error", text: error.response?.data?.message || "Erreur lors du changement d'email" });
      console.error(error);
    } finally {
      setIsEmailSubmitting(false);
    }
  };

  const handleOldEmailCodeChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;

    const newDigits = [...oldEmailCodeDigits];
    newDigits[index] = value;
    setOldEmailCodeDigits(newDigits);

    if (value && index < 5) {
      const nextInput = document.getElementById(`old-email-code-digit-${index + 1}`);
      if (nextInput) nextInput.focus();
    }

    setMessage({ type: "", text: "" });
  };

  const handleOldEmailCodeKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (!oldEmailCodeDigits[index] && index > 0) {
        const prevInput = document.getElementById(`old-email-code-digit-${index - 1}`);
        if (prevInput) {
          prevInput.focus();
          const newDigits = [...oldEmailCodeDigits];
          newDigits[index - 1] = "";
          setOldEmailCodeDigits(newDigits);
        }
      }
    }
  };

  const handleSubmitOldEmailCode = async () => {
    const code = oldEmailCodeDigits.join("");

    if (code.length !== 6) {
      setMessage({ type: "error", text: "Veuillez saisir un code à 6 chiffres" });
      return;
    }

    setIsOldEmailCodeSubmitting(true);

    try {
      const response = await API.put("user/personalUpdate", { uuid: user?.uuid, email: user?.email, type: "emailCode" }, { token: code, newEmail: emailData.newEmail });

      if (response.success) {
        setShowOldEmailCodeInput(false);
        setShowNewEmailCodeInput(true);
        setNewEmailCodeDigits(["", "", "", "", "", ""]);
        setMessage({ type: "info", text: "Code confirmé. Vérifiez votre nouvel email" });
      } else {
        setMessage({ type: "error", text: response.message || "Code de vérification invalide" });
      }
    } catch (error) {
      setMessage({ type: "error", text: error.response?.data?.message || "Erreur lors de la vérification du code" });
      console.error(error);
    } finally {
      setIsOldEmailCodeSubmitting(false);
    }
  };

  const handleNewEmailCodeChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;

    const newDigits = [...newEmailCodeDigits];
    newDigits[index] = value;
    setNewEmailCodeDigits(newDigits);

    if (value && index < 5) {
      const nextInput = document.getElementById(`new-email-code-digit-${index + 1}`);
      if (nextInput) nextInput.focus();
    }

    setMessage({ type: "", text: "" });
  };

  const handleNewEmailCodeKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (!newEmailCodeDigits[index] && index > 0) {
        const prevInput = document.getElementById(`new-email-code-digit-${index - 1}`);
        if (prevInput) {
          prevInput.focus();
          const newDigits = [...newEmailCodeDigits];
          newDigits[index - 1] = "";
          setNewEmailCodeDigits(newDigits);
        }
      }
    }
  };

  const handleCancelEmailCode = () => {
    setShowOldEmailCodeInput(false);
    setShowNewEmailCodeInput(false);
    setOldEmailCodeDigits(["", "", "", "", "", ""]);
    setNewEmailCodeDigits(["", "", "", "", "", ""]);
    setMessage({ type: "", text: "" });
  };

  const handleSubmitNewEmailCode = async () => {
    const code = newEmailCodeDigits.join("");

    if (code.length !== 6) {
      setMessage({ type: "error", text: "Veuillez saisir un code à 6 chiffres" });
      return;
    }

    setIsNewEmailCodeSubmitting(true);

    try {
      const response = await API.put("user/personalUpdate", { uuid: user?.uuid, email: user?.email, type: "emailReCode" }, { oldToken: oldEmailCodeDigits.join(""), token: code, newEmail: emailData.newEmail });

      if (response.success) {
        setEmailData({ oldEmail: emailData.newEmail, newEmail: "", confirmEmail: "" });
        setUser((prev) => ({ ...prev, email: emailData.newEmail }));
        setShowNewEmailCodeInput(false);
        setOldEmailCodeDigits(["", "", "", "", "", ""]);
        setNewEmailCodeDigits(["", "", "", "", "", ""]);
        setMessage({ type: "success", text: "Email changé avec succès !" });
      } else {
        setMessage({ type: "error", text: response.message || "Code de vérification invalide" });
      }
    } catch (error) {
      setMessage({ type: "error", text: error.response?.data?.message || "Erreur lors de la vérification du code" });
      console.error(error);
    } finally {
      setIsNewEmailCodeSubmitting(false);
    }
  };

  const handleConfirm2FA = async () => {
    setIs2FASubmitting(true);
    setMessage({ type: "", text: "" });

    try {
      const newStatus = !a2f_enabled;
      const response = await API.put("user/personalUpdate", { uuid: user?.uuid, email: user?.email, type: "2fa" }, { a2f_enabled: newStatus });

      if (response.success) {
        setA2f_enabled(newStatus);
        setUser((prev) => ({
          ...prev,
          login_metadata: { twoFA_enabled: newStatus },
        }));
        setMessage({ type: "success", text: newStatus ? "Authentification à deux facteurs activée" : "Authentification à deux facteurs désactivée" });
        handleCancel2FA();
      } else {
        setMessage({ type: "error", text: response.message || "Erreur lors de la modification de l'authentification à deux facteurs" });
      }
    } catch (error) {
      setMessage({ type: "error", text: error.response?.data?.message || "Erreur lors de la modification de l'authentification à deux facteurs" });
      console.error(error);
    } finally {
      setIs2FASubmitting(false);
    }
  };

  return (
    <>
      {/* MDP */}
      <div className="profil-section">
        <div className="profil-section-header"><h2>Mot de passe</h2><span className="profil-section-icon">🔒</span></div>

        {!showCodeInput && (
          <form onSubmit={handleUpdatePassword} className="profil-form">
            <div className="profil-form-group">
              <label htmlFor="oldPassword">Ancien mot de passe</label>
              <div className="profil-password-input-wrapper">
                <input type={showPasswords.old ? "text" : "password"} id="oldPassword" name="oldPassword" value={passwordData.oldPassword} onChange={handlePasswordChange} placeholder="Entrez votre ancien mot de passe" />
                <button type="button" className="profil-password-toggle" onClick={() => togglePasswordVisibility("old")}>{showPasswords.old ? "🙈" : "👁️"}</button>
              </div>
            </div>

            <div className="profil-form-group">
              <label htmlFor="newPassword">Nouveau mot de passe</label>
              <div className="profil-password-input-wrapper">
                <input type={showPasswords.new ? "text" : "password"} id="newPassword" name="newPassword" value={passwordData.newPassword} onChange={handlePasswordChange} placeholder="Entrez votre nouveau mot de passe" />
                <button type="button" className="profil-password-toggle" onClick={() => togglePasswordVisibility("new")}>{showPasswords.new ? "🙈" : "👁️"}</button>
              </div>
            </div>

            <div className="profil-form-group">
              <label htmlFor="confirmPassword">Confirmer le nouveau mot de passe</label>
              <div className="profil-password-input-wrapper">
                <input type={showPasswords.confirm ? "text" : "password"} id="confirmPassword" name="confirmPassword" value={passwordData.confirmPassword} onChange={handlePasswordChange} placeholder="Confirmez votre nouveau mot de passe" />
                <button type="button" className="profil-password-toggle" onClick={() => togglePasswordVisibility("confirm")}>{showPasswords.confirm ? "🙈" : "👁️"}</button>
              </div>
            </div>

            <p className="profil-password-hint">Le mot de passe doit contenir au moins 8 caractères</p>
            <button type="submit" className="profil-button profil-button-secondary" disabled={isPasswordSubmitting}>{isPasswordSubmitting ? "Changement..." : "Changer le mot de passe"}</button>
          </form>
        )}

        {showCodeInput && (
          <div className="profil-code-verification">
            <div className="profil-code-header"><h3>Vérification du code</h3><p>Un code de vérification a été envoyé. Saisissez-le ci-dessous.</p></div>

            <div className="profil-code-inputs">
              {codeDigits.map((digit, index) => (
                <input
                  key={index}
                  id={`code-digit-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(index, e)}
                  className="profil-code-input"
                  placeholder="0"
                  autoFocus={index === 0}
                />
              ))}
            </div>

            <div className="profil-code-buttons">
              <button type="button" className="profil-button profil-button-primary" onClick={handleSubmitCode} disabled={isCodeSubmitting}>{isCodeSubmitting ? "Vérification..." : "Valider le code"}</button>
              <button type="button" className="profil-button profil-button-secondary" onClick={handleCancelCode} disabled={isCodeSubmitting}>Annuler</button>
            </div>
          </div>
        )}
      </div>

      {/* EMAIL */}
      <div className="profil-section">
        <div className="profil-section-header"><h2>Adresse Email</h2><span className="profil-section-icon">📧</span></div>

        {!showOldEmailCodeInput && !showNewEmailCodeInput && (
          <form onSubmit={handleUpdateEmail} className="profil-form">
            <div className="profil-form-group">
              <label htmlFor="oldEmail">Email actuel</label>
              <input type="email" id="oldEmail" name="oldEmail" value={emailData.oldEmail} disabled className="profil-input-disabled" />
            </div>

            <div className="profil-form-group">
              <label htmlFor="newEmail">Nouvel email</label>
              <input type="email" id="newEmail" name="newEmail" value={emailData.newEmail} onChange={handleEmailChange} placeholder="Entrez votre nouvel email" />
            </div>

            <div className="profil-form-group">
              <label htmlFor="confirmEmail">Confirmer le nouvel email</label>
              <input type="email" id="confirmEmail" name="confirmEmail" value={emailData.confirmEmail} onChange={handleEmailChange} placeholder="Confirmez votre nouvel email" />
            </div>

            <button type="submit" className="profil-button profil-button-secondary" disabled={isEmailSubmitting}>
              {isEmailSubmitting ? "Changement..." : "Changer l'email"}
            </button>
          </form>
        )}

        {showOldEmailCodeInput && !showNewEmailCodeInput && (
          <div className="profil-code-verification">
            <div className="profil-code-header"><h3>Vérifier votre ancien email</h3><p>Un code de vérification a été envoyé à {emailData.oldEmail}. Saisissez-le ci-dessous.</p></div>

            <div className="profil-code-inputs">
              {oldEmailCodeDigits.map((digit, index) => (
                <input
                  key={index}
                  id={`old-email-code-digit-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOldEmailCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleOldEmailCodeKeyDown(index, e)}
                  className="profil-code-input"
                  placeholder="0"
                  autoFocus={index === 0}
                />
              ))}
            </div>

            <div className="profil-code-buttons">
              <button type="button" className="profil-button profil-button-primary" onClick={handleSubmitOldEmailCode} disabled={isOldEmailCodeSubmitting}>{isOldEmailCodeSubmitting ? "Vérification..." : "Valider le code de l'ancien email"}</button>
              <button type="button" className="profil-button profil-button-secondary" onClick={handleCancelEmailCode} disabled={isOldEmailCodeSubmitting}>Annuler</button>
            </div>
          </div>
        )}

        {showNewEmailCodeInput && (
          <div className="profil-code-verification">
            <div className="profil-code-header"><h3>Vérifier votre nouvel email</h3><p>Un code de vérification a été envoyé à {emailData.newEmail}. Saisissez-le ci-dessous.</p></div>

            <div className="profil-code-inputs">
              {newEmailCodeDigits.map((digit, index) => (
                <input
                  key={index}
                  id={`new-email-code-digit-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleNewEmailCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleNewEmailCodeKeyDown(index, e)}
                  className="profil-code-input"
                  placeholder="0"
                  autoFocus={index === 0}
                />
              ))}
            </div>

            <div className="profil-code-buttons">
              <button type="button" className="profil-button profil-button-primary" onClick={handleSubmitNewEmailCode} disabled={isNewEmailCodeSubmitting}>{isNewEmailCodeSubmitting ? "Vérification..." : "Valider le code du nouvel email"}</button>
              <button type="button" className="profil-button profil-button-secondary" onClick={handleCancelEmailCode} disabled={isNewEmailCodeSubmitting}>Annuler</button>
            </div>
          </div>
        )}
      </div>

      {/* 2FA */}
      <div className="profil-section">
        <div className="profil-section-header"><h2>Authentification à Deux Facteurs</h2><span className="profil-section-icon">🔐</span></div>

        <div className="profil-2fa-content">
          <p className="profil-2fa-description">L'authentification à deux facteurs ajoute une couche supplémentaire de sécurité à votre compte.</p>
          <p className="profil-2fa-status">Statut actuel : <span className={a2f_enabled ? "profil-2fa-enabled" : "profil-2fa-disabled"}>{a2f_enabled ? "✓ Activée" : "✗ Désactivée"}</span></p>

          {!show2FAConfirm && (
            <button type="button" className={a2f_enabled ? "profil-button profil-button-secondary" : "profil-button profil-button-primary"} onClick={handleToggle2FA} disabled={is2FASubmitting}>{a2f_enabled ? "Désactiver 2FA" : "Activer 2FA"}</button>
          )}

          {show2FAConfirm && (
            <div className="profil-2fa-confirmation">
              <div className="profil-2fa-warning"><p>⚠️ Êtes-vous sûr de vouloir {a2f_enabled ? "désactiver" : "activer"} l'authentification à deux facteurs ?</p></div>
              <div className="profil-2fa-buttons">
                <button type="button" className="profil-button profil-button-primary" onClick={handleConfirm2FA} disabled={is2FASubmitting}>{is2FASubmitting ? "Modification..." : "Confirmer"}</button>
                <button type="button" className="profil-button profil-button-secondary" onClick={handleCancel2FA} disabled={is2FASubmitting}>Annuler</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}